import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  Grid,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditableItemList from './common/EditableItemList';
import { toPickerValue, fromPickerValue } from '../../../../utils/dateOnly';
import { useCV } from '../contexts/CVContext';
import { formatDateRange } from '../helpers/ProcessTemplate';

const Courses = () => {
  const { isDataReady, cvData, updateCourses, readonly } = useCV();
  
  // Initialize with empty courses array
  const [courses, setCourses] = useState([]);
  
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.courses?.length > 0 && isDataReady) {
      setCourses(cvData.courses);
    }
  }, [cvData.courses, isDataReady]);
  
  // Custom handler to update both local state and context
  const handleCoursesChange = (newCourses) => {
    // Update the order property for each course
    const orderedCourses = newCourses.map((course, index) => ({
      ...course,
      order: index + 1
    }));
    
    setCourses(orderedCourses);
    updateCourses(orderedCourses);
  };

  // Function to create a new empty course
  const createNewCourse = () => ({
    id: null,
    course: '',
    institution: '',
    startDate: null,
    endDate: null,
    inProgress: false,
    order: courses.length + 1
  });

  // Function to render the summary of a course (collapsed view)
  const renderCourseSummary = (course) => (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {course.course || '(Not specified)'}
      </Typography>
      {course.institution && (
        <Typography variant="body2" color="text.secondary">
          {course.institution}
        </Typography>
      )}
      {(course.startDate || course.endDate || course.inProgress) && (
        <Typography variant="body2" color="text.secondary">
          {formatDateRange(course.startDate, course.endDate, course.inProgress)}
        </Typography>
      )}
    </>
  );

  // Function to render the edit form for a course (expanded view)
  const renderCourseEditForm = (course, onChange) => (
    <Grid 
      container 
      spacing={2}
      sx={{
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        },
        gap: '16px',
        '& .MuiGrid-item': {
          padding: '0',
          width: '100%'
        }
      }}
    >
      <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Course <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={course.course || ''}
            onChange={(e) => onChange({ ...course, course: e.target.value })}
            placeholder="Enter course name"
            size="small"
            autoComplete="new-password"
            name={`cv_course_name_${Math.random().toString(36).substring(2, 9)}`}
            required
            disabled={readonly}
          />
        </Box>
      </Grid>
      
      <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Institution
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={course.institution || ''}
            onChange={(e) => onChange({ ...course, institution: e.target.value })}
            placeholder="Enter institution name"
            size="small"
            autoComplete="new-password"
            name={`cv_course_inst_${Math.random().toString(36).substring(2, 9)}`}
            disabled={readonly}
          />
        </Box>
      </Grid>
      
      <Grid item>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Start Date <span style={{ color: 'red' }}>*</span>
          </Typography>
          <DatePicker
            views={['year', 'month']}
            value={toPickerValue(course.startDate)}
            onChange={(date) => onChange({ ...course, startDate: fromPickerValue(date) })}
            disabled={readonly}
            slotProps={{ 
              textField: { 
                fullWidth: true, 
                size: "small", 
                placeholder: "MM / YYYY",
                required: true
              } 
            }}
          />
        </Box>
      </Grid>
      
      <Grid item>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            End Date
          </Typography>
          <DatePicker
            views={['year', 'month']}
            value={toPickerValue(course.endDate)}
            onChange={(date) => onChange({ ...course, endDate: fromPickerValue(date) })}
            disabled={course.inProgress || readonly}
            slotProps={{ 
              textField: { 
                fullWidth: true, 
                size: "small", 
                placeholder: "MM / YYYY" 
              } 
            }}
          />
          <FormControlLabel
            control={
              <Checkbox 
                checked={course.inProgress || false} 
                onChange={(e) => {
                  const updatedCourse = { 
                    ...course, 
                    inProgress: e.target.checked,
                    endDate: e.target.checked ? null : course.endDate 
                  };
                  onChange(updatedCourse);
                }}
                size="small"
                disabled={readonly}
              />
            }
            label="In Progress"
            sx={{ mt: 1 }}
          />
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <EditableItemList
      items={courses}
      setItems={handleCoursesChange}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      createNewItem={createNewCourse}
      renderSummary={renderCourseSummary}
      renderEditForm={renderCourseEditForm}
      addButtonText={courses.length > 0 ? "Add one more course" : "Add a course"}
      emptyState={{
        title: "You haven't added any courses yet.",
        description: "Add any relevant training or programs you've completed."
      }}
      itemName="course"
      readonly={readonly}
    />
  );
};

export default Courses; 